import { Button } from '@mui/material';
import React, { FC } from 'react';

import { Link, useLocation, useNavigate } from 'react-router-dom';

import { MODAL__POST_DELETE } from '../../../constants/routeModalParams';
import { getOpenModalUrl } from '../../../containers/modals/helpers';
import { Message } from '../../../generated/graphql';
import translate from '../../../i18n/translate';

import MuiIcon from '../../MUI/MuiIcon';

import * as Styled from './styled';

export type Props = {
  messageId: Message['id'];
  shouldShowContent: boolean;
  shouldUseIcon?: boolean;
};

/**
 * Message section which returns delete button if user is authorized to delete the message.
 * If button is clicked, confirmation modal pops up.
 *
 * @param props                   Props passed to the component
 * @param props.messageId         Id of message
 * @param props.shouldShowContent Tells should component return its content or null
 * @param props.shouldUseIcon     If to use a delete icon instead of a delete button
 * @returns                       The component itself or null
 */
const MessageDelete: FC<Props> = ({
  messageId,
  shouldShowContent,
  shouldUseIcon = false,
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const label = translate('GENERAL__DELETE');

  if (shouldShowContent === false) {
    return null;
  }

  const modalUrl = getOpenModalUrl(location, MODAL__POST_DELETE, { messageId });

  /**
   * Handles the delete button click.
   * Navigates to the same page with modal parameters in the URL,
   * which triggers the confirmation modal without reloading the page.
   */
  const handleClick = () => {
    navigate(modalUrl, { replace: false });
  };

  if (shouldUseIcon) {
    return (
      <Button aria-label={label} color="error" component={Link} to={modalUrl}>
        <MuiIcon icon="deleteOutlined" />
      </Button>
    );
  }

  return (
    <Styled.MuiButton
      aria-label={label}
      color="error"
      onClick={handleClick} // Using onClick navigate instead of href to avoid page reload
      startIcon={<MuiIcon icon={'deleteOutlined'} />}
      style={{ justifyContent: 'flex-start' }}
    >
      {label}
    </Styled.MuiButton>
  );
};

export default MessageDelete;
